import {useContext} from 'react';
import {tokens, useTokenValue, RootLayer} from '@sail/ui';
import {
  DefaultTheme,
  DotComTheme,
  DotComThemeChroma,
  DotComThemeCottonCandy,
  DotComThemeLemonLime,
  DotComThemeOvercast,
  DotComThemePomegranate,
  DotComThemeSlate,
  DotComThemeSunburst,
  DotComThemeTropical,
  DotComThemeTwilight,
  DotComThemeWintergreen,
  LinkTheme,
  LinkAssistantTheme,
  type Theme,
  ThemeProvider,
} from '@stripe-internal/conversations-ui';
import {
  Audience,
  SupportEntryPointEntryPoint,
} from '@stripe-internal/conversations-shared-dependencies';
import {ConfigContext} from './ConfigProvider';
import {contrastingTextColor, darkenColor} from '../helpers/colors';
import {isDotComEntryPoint} from '../Screens/ChatScreen/conversationUtils';

type Props = {
  children: JSX.Element;
};

type ConvUIThemeConfig = {
  audience: Audience;
  entrypoint?: SupportEntryPointEntryPoint;
  entryPoint?: SupportEntryPointEntryPoint;
  primaryColor?: string;
  accentColor?: string;
  theme?: string;
  linkBrandName?: string;
};

const defaultThemeByAudience: {[audience: string]: Theme} = {
  link: LinkTheme,
  dot_com: DotComTheme,
};

const validThemeVariantsByAudience: {
  [audience: string]: {[variant: string]: Theme};
} = {
  dot_com: {
    dot_com_Chroma: DotComThemeChroma,
    dot_com_CottonCandy: DotComThemeCottonCandy,
    dot_com_LemonLime: DotComThemeLemonLime,
    dot_com_Overcast: DotComThemeOvercast,
    dot_com_Pomegranate: DotComThemePomegranate,
    dot_com_Slate: DotComThemeSlate,
    dot_com_Sunburst: DotComThemeSunburst,
    dot_com_Tropical: DotComThemeTropical,
    dot_com_Twilight: DotComThemeTwilight,
    dot_com_Wintergreen: DotComThemeWintergreen,
  },
  link: {
    link_assistant: LinkAssistantTheme,
  },
};

export function ConvUIThemeProvider({
  audience,
  entrypoint,
  entryPoint,
  primaryColor,
  accentColor,
  theme,
  linkBrandName,
  children,
}: {
  children: JSX.Element;
} & ConvUIThemeConfig) {
  const {theme: themeValue, overrides} = useConvUITheme({
    audience,
    entrypoint,
    entryPoint,
    primaryColor,
    accentColor,
    theme,
    linkBrandName,
  });

  return (
    <ThemeProvider value={themeValue} overrides={overrides}>
      {children}
    </ThemeProvider>
  );
}

export function useConvUITheme({
  audience,
  entrypoint,
  entryPoint,
  primaryColor,
  accentColor,
  theme,
  linkBrandName,
}: ConvUIThemeConfig): {theme: Theme; overrides: Partial<Theme>} {
  const lightTextColor = useTokenValue(tokens.color.neutral[100]);
  const darkTextColor = useTokenValue(tokens.color.neutral[800]);

  const overrides: Partial<Theme> = {
    ...(primaryColor &&
      accentColor && {
        brandColor: primaryColor,
        onBrandTextColor: contrastingTextColor(
          primaryColor,
          lightTextColor,
          darkTextColor,
        ),
        ctaColor: primaryColor,
        onCtaTextColor: contrastingTextColor(
          primaryColor,
          lightTextColor,
          darkTextColor,
        ),
        ctaHoverColor: darkenColor(primaryColor, 0.8),
        linkColor: accentColor,
        avatarGradientStart: darkenColor(primaryColor, 0.8),
        avatarGradientEnd: primaryColor,
      }),
    ...(linkBrandName && {
      linkBrandName,
      assistantHeaderTitle: `${linkBrandName} Support`,
    }),
  };

  const defaultTheme = defaultThemeByAudience[audience] || DefaultTheme;

  let themeValue = defaultTheme;

  const configEntryPoint = entrypoint ?? entryPoint;
  const isDotCom =
    audience === 'dot_com' ||
    (configEntryPoint && isDotComEntryPoint(configEntryPoint));

  // until we split out the dot_com assistant package, make sure we detect
  // either the audience or the entry point for dot_com.
  if (isDotCom) {
    themeValue = DotComTheme;
  }

  // even though we already checked for dot_com, if they explicitly pass a
  // dot_com theme, still allow this to override what we set above.
  if (!!theme && (theme.startsWith('dot_com') || theme.startsWith('link'))) {
    const themeAudience = isDotCom ? 'dot_com' : audience;
    const validThemeVariants =
      validThemeVariantsByAudience[themeAudience] || {};
    themeValue = validThemeVariants[theme] || themeValue;
  }

  return {theme: themeValue, overrides};
}

export const AppThemeProvider = ({children}: Props) => {
  const config = useContext(ConfigContext);

  return (
    <ConvUIThemeProvider {...config}>
      <RootLayer>{children}</RootLayer>
    </ConvUIThemeProvider>
  );
};
